import React, {
  ReactNode,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  Children,
  forwardRef,
} from "react";
import cn from "classnames";
import { useInView } from "react-intersection-observer";

import c from "../../styles/container.module.scss";
import s from "./Hotspots.module.scss";
import bs from "../../styles/block.spacing.module.scss";

import useMediaQuery from "../../hooks/useMatchMedia";
import Item from "./components/Item";
import BlockBackground from "../Common/BlockBackground";
import BlockIntro from "../Common/BlockIntro";

import {
  StyleVariant,
  InnerSpacingVariant,
  OuterSpacingVariant,
} from "../../lib/storyblok/types/shared";
import { RichTextContent } from "../../lib/storyblok/types/fieldtypes/richText";

type ItemProps = React.ComponentProps<typeof Item>;

interface Props {
  title: string;
  description: RichTextContent;
  buttons?: ReactNode;
  style?: StyleVariant;
  innerSpacing: InnerSpacingVariant;
  outerSpacing: OuterSpacingVariant;
  image: ReactNode;
  inverted?: boolean;
  children: ReactNode;
}

interface ImageWrapperProps {
  children: ReactNode;
  onImageLoad?: (width: number, height: number) => void;
}

const isHotspotItem = (
  node: ReactNode
): node is React.ReactElement<ItemProps> =>
  React.isValidElement(node) &&
  (node.type as { displayName?: string }).displayName === "Item";

const ImageWrapper = forwardRef<HTMLImageElement, ImageWrapperProps>(
  ({ children, onImageLoad }, ref) => {
    const imageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!ref) return;

      const findImageElement = (
        node: HTMLElement | null
      ): HTMLImageElement | null => {
        if (!node) return null;

        if (node.tagName && node.tagName.toLowerCase() === "img") {
          return node as HTMLImageElement;
        }

        if (node.querySelector) {
          const img = node.querySelector("img");
          if (img) return img as HTMLImageElement;
        }

        return null;
      };

      setTimeout(() => {
        if (imageRef.current) {
          const imgElement = findImageElement(imageRef.current);
          if (imgElement) {
            if (typeof ref === "function") {
              ref(imgElement);
            } else if (ref) {
              ref.current = imgElement;
            }

            if (onImageLoad && imgElement.complete) {
              onImageLoad(imgElement.naturalWidth, imgElement.naturalHeight);
            } else if (onImageLoad) {
              const handleLoad = () => {
                onImageLoad(imgElement.naturalWidth, imgElement.naturalHeight);
                imgElement.removeEventListener("load", handleLoad);
              };
              imgElement.addEventListener("load", handleLoad);
            }
          }
        }
      }, 0);
    }, [ref, onImageLoad]);

    return <div ref={imageRef}>{children}</div>;
  }
);

ImageWrapper.displayName = "ImageWrapper";

const Hotspots = ({
  title,
  description,
  buttons,
  style = "none",
  innerSpacing,
  outerSpacing,
  image,
  inverted,
  children,
}: Props) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasTriggeredRandom = useRef(false);

  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const hotspots = useMemo(
    () => Children.toArray(children).filter(isHotspotItem),
    [children]
  );

  const [activeSpot, setActiveSpot] = useState<string | null>(null);

  const handleImageLoad = useCallback((width: number, height: number) => {
    setImageDimensions({ width, height });
    if (width < 1400 && contentRef.current) {
      contentRef.current.style.maxWidth = `${width}px`;
      contentRef.current.style.margin = "0 auto";
    }
  }, []);

  useEffect(() => {
    if (inView && hotspots.length && !hasTriggeredRandom.current) {
      const randomIndex = Math.floor(Math.random() * hotspots.length);
      const randomHotspot = hotspots[
        randomIndex
      ] as React.ReactElement<ItemProps>;

      setTimeout(() => {
        setActiveSpot(randomHotspot.props._uid);
        hasTriggeredRandom.current = true;
      }, 1000);
    }
  }, [inView, hotspots]);

  const toggleSpot = useCallback(
    (uid: string) => setActiveSpot((prev) => (prev === uid ? null : uid)),
    []
  );

  const renderHotspot = useCallback(
    (
      child: React.ReactElement<ItemProps>,
      variant: "dot" | "list" | "desktop"
    ) =>
      React.cloneElement(child, {
        key: child.props._uid,
        onSpotClick: toggleSpot,
        isActive: child.props._uid === activeSpot,
        variant,
      }),
    [toggleSpot, activeSpot]
  );

  const handleOutsideClick = useCallback(
    (e: React.MouseEvent) => {
      if (activeSpot && e.target === e.currentTarget) {
        setActiveSpot(null);
      }
    },
    [activeSpot]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && activeSpot) {
        setActiveSpot(null);
      }
    },
    [activeSpot]
  );

  const overlay = (
    <>
      <div
        className={cn(s.imageContainer)}
        ref={imgWrapRef}
        onClick={handleOutsideClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label="Image with interactive hotspots"
      >
        <ImageWrapper ref={imageRef} onImageLoad={handleImageLoad}>
          {image}
        </ImageWrapper>

        <div className={s.spotsOverlay}>
          {hotspots.map((child) =>
            renderHotspot(
              child as React.ReactElement<ItemProps>,
              isMobile ? "dot" : "desktop"
            )
          )}
        </div>
      </div>

      {isMobile && (
        <div
          className={s.mobileSpots}
          role="tablist"
          aria-label="Hotspot information list"
        >
          {hotspots.map((child) =>
            renderHotspot(child as React.ReactElement<ItemProps>, "list")
          )}
        </div>
      )}
    </>
  );

  useEffect(() => {
    if (!imageDimensions || !contentRef.current) return;

    const handleResize = () => {
      if (contentRef.current) {
        if (window.innerWidth < imageDimensions.width) {
          contentRef.current.style.maxWidth = "";
        } else if (imageDimensions.width < 1400) {
          contentRef.current.style.maxWidth = `${imageDimensions.width}px`;
        }
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [imageDimensions]);

  return (
    <section
      ref={ref}
      className={cn(s.hotspots, bs[innerSpacing], bs[outerSpacing])}
    >
      <BlockBackground style={style} opacity={100} />

      <div className={s.hotspotsInner}>
        <BlockIntro
          alignment="alignCenter"
          inverted={inverted}
          title={title}
          description={description}
          buttons={buttons}
          width="contained"
        />
      </div>

      <div ref={contentRef} className={cn(c.xWide, s.hotspotsContent)}>
        {overlay}
      </div>
    </section>
  );
};

Hotspots.Item = Item;
export default Hotspots;
